import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { applyContentAlignmentClass } from './ids-accordion-common';

import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsElement from '../../core/ids-element';

import styles from './ids-accordion-header.scss';
import IdsText from '../ids-text/ids-text';

import type IdsIcon from '../ids-icon/ids-icon';
import type IdsAccordionPanel from './ids-accordion-panel';

// Expander Types
const EXPANDER_TYPES = ['caret', 'plus-minus'];

// Default Icons
const DEFAULT_ICON_OFF = 'caret-down';

// Submenu Style Icons
const ICON_MINUS = 'plusminus-folder-closed';
const ICON_PLUS = 'plusminus-folder-open';

const Base = IdsColorVariantMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS Accordion Header Component
 * @type {IdsAccordionHeader}
 * @inherits IdsElement
 * @mixes IdsColorVariantMixin
 * @mixes IdsEventsMixin
 * @part expander - this accoridon header's expander button element
 * @part header - the accordion header's root element
 * @part icon - the accordion header's icon element
 */
@customElement('ids-accordion-header')
@scss(styles)
export default class IdsAccordionHeader extends Base {
  depth = 0;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute(htmlAttributes.ROLE, 'button');
    this.setAttribute(attributes.TABINDEX, '0');
    this.refreshIconDisplay(this.icon);
    this.#refreshExpanderIconType();
    this.refreshDepth();
    this.refreshPlacement();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<any> {
    return [
      ...super.attributes,
      attributes.BORDERLESS,
      attributes.CHILD_FILTER_MATCH,
      attributes.DISABLED,
      attributes.EXPANDED,
      attributes.EXPANDER_TYPE,
      attributes.HEADER_FILL,
      attributes.HIDDEN_BY_FILTER,
      attributes.ICON,
      attributes.SELECTED
    ];
  }

  /**
   * @returns {Array<string>} List of available color variants for this component
   */
  colorVariants = [
    'app-menu',
    'sub-app-menu',
    'module-nav',
    'sub-module-nav'
  ];

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template(): string {
    return `
      <div class="ids-accordion-header" part="header">
        <ids-icon class="ids-accordion-display-icon" part="display-icon"></ids-icon>
        <slot></slot>
        ${this.templateExpanderIcon()}
      </div>
    `;
  }

  /**
   * Expander Icon template contents
   * @returns {string} the Expander Icon template
   */
  templateExpanderIcon(): string {
    return `
      <ids-text audible="true" translate-text="true">AccordionExpand</ids-text>
      <ids-icon class="ids-accordion-expander-icon" icon=${DEFAULT_ICON_OFF} part="expander-icon"></ids-icon>
    `;
  }

  /**
   * @returns {boolean} true if this header component's panel is nested
   */
  get parentHasIcon() {
    let panel = this.panel;
    if (!panel) return false;
    let parent = panel.parentElement;

    while (parent && parent.tagName === 'IDS-ACCORDION-PANEL') {
      if (parent.header && (parent.header.icon || parent.container.classList.contains('parent-has-icon'))) {
        return true;
      }
      panel = parent;
      parent = panel.parentElement;
    }

    return false;
  }

  /**
   * @returns {boolean} if this header component
   */
  get siblingsCanExpand() {
    const panel = this.panel;
    if (!panel) return false;
    const parent = <IdsAccordionPanel>panel.parentElement;

    let canExpand = false;
    parent.childPanels?.forEach((childPanel: IdsAccordionPanel) => {
      if (!canExpand && childPanel.isExpandable) canExpand = true;
    });

    return canExpand;
  }

  /**
   * @readonly
   * @returns {IdsText | null} this Accordion Header's text node
   */
  get textNode(): IdsText | null {
    return this.querySelector<IdsText>('ids-text, span');
  }

  /**
   * @readonly
   * @returns {HTMLElement} this header's panel
   */
  get panel(): any {
    return this.parentElement;
  }

  /**
   * @param {string} thisAlignment the type of alignment to apply
   */
  refreshContentAlignment(thisAlignment: any) {
    if (this.container) applyContentAlignmentClass(this.container.classList, thisAlignment);
  }

  /**
   * @returns {boolean} true if this header's pane wrapper is expanded
   */
  get expanded(): boolean {
    return this.panel?.expanded || false;
  }

  /**
   * @param {boolean} val true if this header should appear expanded
   */
  set expanded(val: boolean | string | null) {
    const trueVal = stringToBool(val);

    if (trueVal) {
      this.triggerEvent('beforeexpanded', this, { bubbles: true });
    } else {
      this.triggerEvent('beforecollapsed', this, { bubbles: true });
    }

    this.container?.classList[trueVal ? 'add' : 'remove']('expanded');
    if (this.panel) this.panel.expanded = trueVal;

    if (trueVal) {
      this.triggerEvent('expanded', this, { bubbles: true });
    } else {
      this.triggerEvent('collapsed', this, { bubbles: true });
    }

    this.#refreshExpanderIconType();
  }

  /**
   * @returns {string} the current expander type
   */
  get expanderType(): string | null {
    return this.getAttribute(attributes.EXPANDER_TYPE);
  }

  /**
   * @param {string} val the type of expander to use
   */
  set expanderType(val: string | null) {
    const currentVal = this.expanderType;
    let trueVal = EXPANDER_TYPES[0];
    if (val && EXPANDER_TYPES.includes(val)) {
      trueVal = val;
    }

    if (currentVal !== trueVal) {
      this.setAttribute(attributes.EXPANDER_TYPE, String(val));
      this.toggleExpanderIcon(trueVal);
      this.#refreshExpanderIconClass(currentVal, trueVal);
    }
  }

  #refreshExpanderIconClass(oldType: any, newType: any) {
    const cl = this.container?.classList;
    const oldTypeClass = `expander-type-${oldType}`;
    const newTypeClass = `expander-type-${newType}`;
    if (oldType) cl?.remove(oldTypeClass);
    if (newType) cl?.add(newTypeClass);
  }

  /**
   * Focuses this accordion header
   */
  focus(): void {
    super.focus();
  }

  /**
   * @returns {string} the currently-displayed icon, if applicable
   */
  get icon() {
    return this.getAttribute(attributes.ICON);
  }

  /**
   * @param {string} val the type of display icon to show
   */
  set icon(val: string | null) {
    if (this.icon !== val) {
      if (typeof val !== 'string' || !val.length) {
        this.removeAttribute(attributes.ICON);
      } else {
        this.setAttribute(attributes.ICON, `${val}`);
      }

      this.refreshIconDisplay(val);
    }
  }

  /**
   * @param {string} val the icon definition to apply
   */
  refreshIconDisplay(val: string | any[] | null) {
    const iconDef = typeof val === 'string' && val.length ? val : '';
    const iconElem = this.container?.querySelector<IdsIcon>('.ids-accordion-display-icon');

    if (iconElem) {
      iconElem.setAttribute('icon', iconDef);
      this.container?.classList.toggle('has-icon', !!iconDef.length);
    } else {
      this.container?.classList.remove('has-icon');
    }

    const hasParentIcon = this.parentHasIcon;
    const siblingsCanExpand = this.siblingsCanExpand;
    const expandable = this.panel?.isExpandable || false;

    this.container?.classList.toggle('parent-has-icon', !!hasParentIcon);
    this.container?.classList.toggle('is-expandable', !!expandable);
    this.container?.classList.toggle('siblings-can-expand', !!siblingsCanExpand);
  }

  /**
   * @returns {boolean} true if this accordion header should appear "selected"
   */
  get selected(): boolean {
    return stringToBool(this.getAttribute(attributes.SELECTED));
  }

  /**
   * @param {boolean} val true if this accordion header should appear "selected"
   */
  set selected(val: boolean) {
    const currentValue = this.selected;
    const isValueTruthy = stringToBool(val);

    if (currentValue !== isValueTruthy) {
      if (isValueTruthy) {
        this.setAttribute(attributes.SELECTED, `${val}`);
      } else {
        this.removeAttribute(attributes.SELECTED);
      }
      this.#refreshSelected(isValueTruthy);

      if (isValueTruthy) {
        this.triggerEvent('selected', this, {
          bubbles: true,
          detail: {
            elem: this,
          }
        });
        this.triggerEvent('deselect', this, {
          bubbles: true,
          detail: {
            elem: this,
          }
        });
      }
    }
  }

  /**
   * Refreshes the visual "Selected" state
   * @param {boolean} isSelected true if the Accordion Header should appear "Selected"
   */
  #refreshSelected(isSelected: any) {
    this.container?.classList[isSelected ? 'add' : 'remove']('selected');

    const textNode = this.textNode;
    if (textNode && this.colorVariant === 'app-menu') {
      textNode.fontWeight = isSelected ? 'bold' : null;
    }
  }

  /**
   * Toggles the display of an expander icon
   * @param {boolean} val true if the expander icon should be displayed
   */
  toggleExpanderIcon(val: string | boolean) {
    if (this.panel?.isExpandable && stringToBool(val)) {
      this.#showExpanderIcon();
    } else {
      this.#hideExpanderIcon();
    }
  }

  get accordion(): HTMLElement | null {
    let currentElement: HTMLElement | null = this.parentElement;
    while (currentElement && currentElement.tagName !== 'IDS-ACCORDION') {
      currentElement = currentElement.parentElement;
    }
    return currentElement;
  }

  /**
   * Renders the expander icon, either adding it to the DOM or updating if it exists.
   */
  #showExpanderIcon(): void {
    const keepExpanderPlacement = this.accordion?.hasAttribute(attributes.KEEP_EXPANDER_PLACEMENT);
    const appendLocation = this.panel?.hasParentPanel && !keepExpanderPlacement ? 'afterbegin' : 'beforeend';
    const expander = this.container?.querySelector('.ids-accordion-expander-icon');

    if (!expander) {
      // Apply the expander button in front of the text
      // for any variants prefixed with `sub-`.
      const expanderIcon = this.templateExpanderIcon();
      this.container?.insertAdjacentHTML(appendLocation, expanderIcon);
    } else if (appendLocation === 'afterbegin') {
      this.container?.prepend(expander);
    } else {
      this.container?.append(expander);
    }

    this.#refreshExpanderIconType();
  }

  /**
   * Removes the expander icon from the DOM.
   * @returns {void}
   */
  #hideExpanderIcon(): void {
    this.container?.querySelector('.ids-accordion-expander-icon')?.remove();
  }

  /**
   * Changes the visual style of the expander icon
   * @returns {void}
   */
  #refreshExpanderIconType(): void {
    const icon = this.container?.querySelector('.ids-accordion-expander-icon');
    if (!icon) {
      return;
    }

    let iconType = DEFAULT_ICON_OFF;
    if (this.expanderType === 'plus-minus') {
      iconType = this.expanded ? ICON_PLUS : ICON_MINUS;
    }
    icon.setAttribute('icon', iconType);
    this.#refreshExpanderIconClass(null, this.expanderType);
  }

  /**
   * @param {boolean} val true if this accordion header should appear to be "filtered",
   *  which usually means "hidden"
   */
  set hiddenByFilter(val: boolean) {
    if (stringToBool(val)) {
      this.setAttribute(attributes.HIDDEN_BY_FILTER, '');
      this.container?.classList.add(attributes.HIDDEN_BY_FILTER);
    } else {
      this.removeAttribute(attributes.HIDDEN_BY_FILTER);
      this.container?.classList.remove(attributes.HIDDEN_BY_FILTER);
    }
  }

  /**
   * @returns {boolean} true if this accordion header is currently displayed as "filtered"
   */
  get hiddenByFilter(): boolean {
    return this.hasAttribute(attributes.HIDDEN_BY_FILTER);
  }

  /**
   * @param {boolean} val true if this accordion header's panel contains a child panel
   * that matches the specified filter term, and should be displayed accordingly
   */
  set childFilterMatch(val: boolean) {
    if (stringToBool(val)) {
      this.setAttribute(attributes.CHILD_FILTER_MATCH, '');
      this.container?.classList.add(attributes.CHILD_FILTER_MATCH);
    } else {
      this.removeAttribute(attributes.CHILD_FILTER_MATCH);
      this.container?.classList.remove(attributes.CHILD_FILTER_MATCH);
    }
  }

  /**
   * @returns {boolean} true if this accordion header's panel contains a child panel
   * that matches the specified filter term, and should be displayed accordingly
   */
  get childFilterMatch(): boolean {
    return this.hasAttribute(attributes.HIDDEN_BY_FILTER);
  }

  /**
   * Sets disabled property
   * @param {boolean | string} value true/false
   */
  set disabled(value: boolean | string) {
    const disabled = stringToBool(value);

    if (disabled) {
      this.setAttribute(attributes.DISABLED, `${disabled}`);
      this.container?.classList.add(attributes.DISABLED);
    } else {
      this.removeAttribute(attributes.DISABLED);
      this.container?.classList.remove(attributes.DISABLED);
    }
  }

  get disabled(): boolean | string {
    return stringToBool(this.getAttribute(attributes.DISABLED));
  }

  /**
   * Sets header fill property
   * @param {boolean | string} value true/false
   */
  set headerFill(value: boolean | string) {
    const headerFill = stringToBool(value);

    if (headerFill) {
      this.setAttribute(attributes.HEADER_FILL, `${headerFill}`);
      this.container?.classList.add(attributes.HEADER_FILL);
    } else {
      this.removeAttribute(attributes.HEADER_FILL);
      this.container?.classList.remove(attributes.HEADER_FILL);
    }
  }

  /**
   * Gets header fill property
   * @returns {boolean} true if header fill is enabled
   */
  get headerFill(): boolean {
    return stringToBool(this.getAttribute(attributes.HEADER_FILL));
  }

  /**
   * Sets borderless property
   * @param {boolean | string} value true to hide bottom border, false to show it
   */
  set borderless(value: boolean | string) {
    const borderless = stringToBool(value);

    if (borderless) {
      this.setAttribute(attributes.BORDERLESS, '');
      this.container?.classList.add(attributes.BORDERLESS);
    } else {
      this.removeAttribute(attributes.BORDERLESS);
      this.container?.classList.remove(attributes.BORDERLESS);
    }
  }

  /**
   * Gets borderless property
   * @returns {boolean} true if header border is hidden
   */
  get borderless(): boolean {
    return this.hasAttribute(attributes.BORDERLESS);
  }

  refreshDepth() {
    if (!this.container) return;

    let depth = 0;
    let currentEl: HTMLElement = this;

    while (currentEl.parentElement && currentEl.parentElement.tagName === 'IDS-ACCORDION-PANEL') {
      depth += 1;
      currentEl = currentEl.parentElement;
    }

    if (this.depth) this.container.classList.remove(`depth-${this.depth}`);

    this.depth = depth;
    if (depth > 0) this.container.classList.add(`depth-${depth}`);
  }

  refreshPlacement() {
    if (!this.container) return;

    const panel = this.panel;
    if (!panel) return;
    const isFirstChild = !panel.previousElementSibling || (panel.previousElementSibling && panel.previousElementSibling.tagName !== 'IDS-ACCORDION-PANEL');
    const isLastChild = !panel.nextElementSibling;

    this.container!.classList[isFirstChild ? 'add' : 'remove']('first-child');
    this.container!.classList[isLastChild ? 'add' : 'remove']('last-child');
  }
}
